import { useState, useEffect } from 'react';
import { useDebounce } from 'use-debounce';

interface UseDebouncedFilterProps {
  filterKey: string;
  currentValue: string;
  onFilterChange: (filterKey: string, value: string) => void;
  debounceMs?: number;
}

interface UseDebouncedFilterReturn {
  inputValue: string;
  setInputValue: (value: string) => void;
  debouncedValue: string;
}

export const useDebouncedFilter = ({
  filterKey,
  currentValue,
  onFilterChange,
  debounceMs = 300,
}: UseDebouncedFilterProps): UseDebouncedFilterReturn => {
  const [inputValue, setInputValue] = useState(currentValue);
  const [debouncedValue] = useDebounce(inputValue, debounceMs);

  // Sync debounced value to URL (triggers API calls)
  useEffect(() => {
    if (debouncedValue !== currentValue) {
      onFilterChange(filterKey, debouncedValue);
    }
  }, [debouncedValue, currentValue, onFilterChange, filterKey]);

  // Initialize input from URL (for browser back/forward, bookmarks, etc.)
  useEffect(() => {
    if (currentValue !== inputValue && currentValue !== debouncedValue) {
      setInputValue(currentValue);
    }
  }, [currentValue]);

  return {
    inputValue,
    setInputValue,
    debouncedValue,
  };
};
